import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
// eslint-disable-next-line import/extensions
import { MapView as MapViewImpl } from './MapView'; // platform-specific: MapView.native.js / MapView.web.js
import { LazyMemoizedSiteMarker } from './LazyMemoizedSiteMarker';
import { Site } from '../../types/Site';
import { MapViewRegion } from '../../types/MapViewRegion';

const styles = StyleSheet.create({
  container: { flex: 1 },
  map: { flex: 1 },
});

export interface SiteMapProps {
  region: MapViewRegion;
  sites: Site[];
  selectedSiteId?: string | null;
  onRegionChangeComplete: (region: MapViewRegion) => void;
  onMarkerPress: (site: Site) => void;
  // Tapping the map background (not a marker) -- mirrors PantryMap.js's handleMapPress, used to
  // dismiss a selected site's preview/details.
  onPress?: () => void;
}

export function SiteMap({
  region,
  sites,
  selectedSiteId,
  onRegionChangeComplete,
  onMarkerPress,
  onPress,
}: SiteMapProps) {
  const mapViewRef = useRef(null);
  const isInitialRegionRef = useRef(true);

  // `initialRegion` (below) is only applied once, on mount -- react-native-maps ignores it on
  // later renders. Pushing subsequent region changes into the map requires the imperative
  // animateToRegion handle instead. But naively animating on every region change would also fire
  // right after the user's own pan/zoom gesture (onRegionChangeComplete below already reflects
  // where the map moved itself to), fighting the gesture -- so the handler decorates the region
  // it forwards, and the effect skips animating for decorated regions. Mirrors PantryMap.js's
  // fromOnChangeComplete guard.
  const handleRegionChangeComplete = useCallback(
    (newRegion: MapViewRegion) => {
      // eslint-disable-next-line no-param-reassign
      newRegion.fromOnChangeComplete = true;
      onRegionChangeComplete(newRegion);
    },
    [onRegionChangeComplete]
  );

  useEffect(() => {
    // The very first region is already applied via initialRegion above -- animating to it too
    // would be redundant (harmless, but an unnecessary bridge call on every mount).
    if (isInitialRegionRef.current) {
      isInitialRegionRef.current = false;
      return;
    }
    if (!region.fromOnChangeComplete) {
      (mapViewRef.current as any)?.animateToRegion(region);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on region.identity, matching PantryMap.js's equivalent effect
  }, [region.identity]);

  return (
    <View style={styles.container} testID="site-map-container">
      <MapViewImpl
        style={styles.map}
        initialRegion={{ ...region }}
        mapViewRef={mapViewRef}
        onRegionChangeComplete={handleRegionChangeComplete}
        onPress={onPress}
      >
        {/* onMarkerPress is passed through unwrapped (not `() => onMarkerPress(site)`) so its
            reference stays stable across renders -- SiteMarker's React.memo can only skip
            re-rendering unchanged markers if none of its props (including this one) are a new
            function/object on every render. SiteMarker binds `site` itself internally. */}
        {sites.map((site) => (
          <LazyMemoizedSiteMarker
            key={site.siteId}
            site={site}
            isSelected={site.siteId === selectedSiteId}
            onPress={onMarkerPress}
          />
        ))}
      </MapViewImpl>
    </View>
  );
}
